import { type ReactElement, type ReactNode, useCallback, useId, useRef, useState } from 'react';
import type { IconName } from '../icons';
import { useIsCompact } from '../layers/compact';
import type { LayerCloseReason } from '../layers/types';
import { useLayer } from '../layers/useLayer';
import { lazyPopup, prefetchOnIdle, useLayerTrigger, useMountedWhileOpen } from './layer-trigger';
import type { Placement } from './Popover';

/** An action row. `checked` makes it a checkbox item (role `menuitemcheckbox`). */
export type MenuAction = {
  type?: 'item';
  label: string;
  icon?: IconName;
  /** Leading custom node (avatar, colour dot) instead of an icon. */
  leading?: ReactNode;
  /** Secondary line under the label. */
  description?: string;
  /** Right aligned meta text (probability, count …). */
  meta?: string;
  /** Platform-aware shortcut shown in the shortcut column, e.g. `mod+d`. */
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  checked?: boolean;
  /** Keep the menu open after selecting (toggles in a list). */
  keepOpen?: boolean;
  onSelect: () => void;
};

export type MenuItem =
  | MenuAction
  | { type: 'separator' }
  /** Section heading; the items after it (up to the next separator or label) form a group. */
  | { type: 'label'; label: string }
  | { type: 'submenu'; label: string; icon?: IconName; disabled?: boolean; items: MenuItem[] }
  | {
      type: 'radio';
      /** Optional group heading. */
      label?: string;
      value: string;
      onValueChange: (value: string) => void;
      options: Array<{ value: string; label: string; icon?: IconName; disabled?: boolean }>;
      keepOpen?: boolean;
    };

export type MenuProps = {
  trigger: ReactElement;
  items: MenuItem[];
  placement?: Placement;
  label?: string;
  width?: number;
  header?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean, reason?: LayerCloseReason) => void;
  nativeButton?: boolean;
};

export function useMenuState(
  controlled: boolean | undefined,
  onOpenChange: MenuProps['onOpenChange'],
): [boolean, (next: boolean, reason?: LayerCloseReason) => void] {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlled ?? uncontrolled;
  const setOpen = useCallback(
    (next: boolean, reason?: LayerCloseReason) => {
      if (controlled === undefined) setUncontrolled(next);
      onOpenChange?.(next, reason);
    },
    [controlled, onOpenChange],
  );
  return [open, setOpen];
}

const { Popup: MenuPopup, prefetch, load: loadPopup } = lazyPopup(() => import('./MenuPopup'));
let idlePrefetched = false;

/**
 * Dropdown menu on the layer stack (Base UI Menu, loaded on first use): ↑ ↓ Home End, typeahead,
 * submenus (→ ←), checkbox and radio items, sections, shortcut column, danger items. Opening with the
 * keyboard (Enter, Space, ↓) focuses the first item, ↑ the last. Below 640 px: a bottom sheet.
 */
export function Menu({
  trigger,
  items,
  placement = 'bottom-start',
  label,
  width,
  header,
  open: controlled,
  onOpenChange,
  nativeButton,
}: MenuProps) {
  const [open, setOpen] = useMenuState(controlled, onOpenChange);
  const compact = useIsCompact();
  const popupId = useId();
  const focusOnOpen = useRef<'first' | 'last' | null>(null);
  if (!idlePrefetched) {
    idlePrefetched = true;
    prefetchOnIdle(loadPopup);
  }
  const triggerElRef = useRef<HTMLElement | null>(null);
  const layer = useLayer({
    kind: compact ? 'sheet' : 'menu',
    open,
    onClose: (reason) => setOpen(false, reason),
    escape: 'self',
    label,
    getElements: () => [triggerElRef.current],
  });
  const { element, triggerEl } = useLayerTrigger({
    trigger,
    open,
    onToggle: (via) => {
      if (open) return void layer.requestClose('programmatic');
      focusOnOpen.current = via === 'keyboard' ? 'first' : null;
      setOpen(true);
    },
    onArrow: (which) => {
      focusOnOpen.current = which;
      setOpen(true);
    },
    haspopup: 'menu',
    popupId,
    nativeButton,
    prefetch,
  });
  triggerElRef.current = triggerEl;
  const [mounted, exited] = useMountedWhileOpen(open);
  return (
    <>
      {element}
      {mounted && triggerEl ? (
        <MenuPopup
          open={open}
          popupId={popupId}
          anchor={triggerEl}
          layer={layer}
          compact={compact}
          items={items}
          placement={placement}
          label={label}
          width={width}
          header={header}
          focusOnOpen={focusOnOpen}
          onRequestOpenChange={setOpen}
          onExited={exited}
        />
      ) : null}
    </>
  );
}
